import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {isFileConfigVariable} from 'analysis-tool/templates/AnalysisTemplateVariableUtils';
import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ConnectionFilesTable from 'instruments/connection-files/ConnectionFilesTable';
import {
  GetAnalysisTemplateForAssayRun_analysisTemplate_AnalysisTemplateNode as AnalysisTemplateNode,
  GetAnalysisTemplateForAssayRun_analysisTemplate_AnalysisTemplateNode_configVariables_AnalysisTemplateVariableNode as AnalysisTemplateConfigVariableNode,
} from 'results/analysis-integration/__generated/GetAnalysisTemplateForAssayRun';
import SearchInput from 'search/inputs/SearchInput';
import {Filter, FilterKey, Operator} from 'search/SearchConstants';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import Stepper, {StepperStep} from 'ui-library/stepper/Stepper';
import omitKey from 'util/omitKey';

import {FileListingGraphQL} from '../../../graphql-fragments/files/__generated/FileListingGraphQL';

export interface ConfigureInputFileFiltersModalProps {
  analysisTemplate: AnalysisTemplateNode;
  initialDefaultFiltersMapping: {[configVariableId: string]: Array<Filter>} | null;
  onSaveDefaultFiltersMapping: (
    newDefaultFiltersMapping: {[configVariableId: string]: Array<Filter>} | null
  ) => void;
  onRequestClose: () => void;
}

export default function ConfigureInputFileFiltersModal(
  props: ConfigureInputFileFiltersModalProps
): JSX.Element {
  const {analysisTemplate, initialDefaultFiltersMapping, onSaveDefaultFiltersMapping, onRequestClose} = props;
  // Which file variable's filters is currently being configured
  const [selectedVariable, _setSelectedVariable] = useState<AnalysisTemplateConfigVariableNode | null>(null);
  // Which connection IDs are being used to filter ConnectionFilesTable
  const [selectedConnectionIds, _setSelectedConnectionIds] = useState<Array<string>>([]);
  // The filter configuration that will be persisted on save. This is updated by onSetSelectedConnectionIds
  // when the user selects or deselects connections to filter by.
  const [draftDefaultFiltersMapping, setDraftDefaultFiltersMapping] = useState<{
    [configVariableId: string]: Array<Filter>;
  } | null>(initialDefaultFiltersMapping);
  // State management for ConnectionFilesTable
  const [connectionFiles, setConnectionFiles] = useState<Array<FileListingGraphQL>>([]);

  // When selectedConnectionIds is updated, then also update draftDefaultFiltersMapping
  function onSetSelectedConnectionIds(newSelectedConnectionIds: Array<string>): void {
    _setSelectedConnectionIds(newSelectedConnectionIds);

    const variableKeyToUpdate = selectedVariable?.id;
    assert(variableKeyToUpdate != null, 'User was able to configure filters for a null config variable.');
    if (
      newSelectedConnectionIds.length === 0 &&
      draftDefaultFiltersMapping != null &&
      draftDefaultFiltersMapping.hasOwnProperty(variableKeyToUpdate)
    ) {
      setDraftDefaultFiltersMapping(omitKey(draftDefaultFiltersMapping, variableKeyToUpdate));
    } else if (newSelectedConnectionIds.length > 0) {
      // This overwrites the whole filters list mapped to variableKeyToUpdate, since we currently only support
      // configuring a default connectionIds filter.
      setDraftDefaultFiltersMapping({
        ...(draftDefaultFiltersMapping ?? {}),
        [variableKeyToUpdate]: [
          {
            key: FilterKey.LINEAGE_PARENT_API_IDENTIFIER,
            operator: Operator.IS_ONE_OF,
            value: newSelectedConnectionIds,
          },
        ],
      });
    }
  }

  // When selectedVariable is updated, then also set the already configured connection ids if they exist,
  // or else reset the selected connection ids
  function onSetSelectedVariable(newSelectedVariable: AnalysisTemplateConfigVariableNode | null): void {
    _setSelectedVariable(newSelectedVariable);

    if (
      newSelectedVariable != null &&
      draftDefaultFiltersMapping != null &&
      draftDefaultFiltersMapping.hasOwnProperty(newSelectedVariable.id)
    ) {
      const connectionIdFilter = draftDefaultFiltersMapping[newSelectedVariable.id].find(
        (filter) => filter.key === FilterKey.LINEAGE_PARENT_API_IDENTIFIER
      )!;
      _setSelectedConnectionIds(connectionIdFilter.value as Array<string>);
    } else {
      _setSelectedConnectionIds([]);
    }
  }

  return (
    <Modal
      large={true}
      enableRedesign={true}
      title="Configure default data source filters"
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        onSaveDefaultFiltersMapping(
          draftDefaultFiltersMapping != null && Object.keys(draftDefaultFiltersMapping).length > 0
            ? draftDefaultFiltersMapping
            : null
        );
        onRequestClose();
      }}
      buttons={[
        {text: 'Cancel', cancel: true},
        {
          text: 'Save',
          submit: true,
          disabled: false,
        },
      ]}
    >
      <Stepper>
        <StepperStep title="Select input file field to configure">
          <SelectDropdown
            displayName={selectedVariable?.name ?? 'Select an input file field...'}
            menu={
              <Menu width={MenuWidth.FULL}>
                {analysisTemplate.configVariables
                  .filter((variable) => isFileConfigVariable(variable))
                  .map((variable) => (
                    <Option
                      key={variable.id}
                      onSelect={() => {
                        onSetSelectedVariable(variable);
                      }}
                    >
                      {variable.name}
                    </Option>
                  ))}
              </Menu>
            }
          />
        </StepperStep>
        {selectedVariable && (
          <StepperStep title="Configure connections filter and preview files">
            <div className={css(styles.connectionTabBody)}>
              <div>
                <FieldLabel label="Connections to filter by" />
                <SearchInput
                  autoFocus={false}
                  types={[ModelTypeConstants.CONNECTION]}
                  selected={selectedConnectionIds}
                  onSelect={(connectionId) => {
                    if (connectionId == null) {
                      return;
                    }
                    onSetSelectedConnectionIds([...selectedConnectionIds, connectionId]);
                  }}
                  onRemove={(connectionId) => {
                    if (connectionId == null) {
                      return;
                    }
                    onSetSelectedConnectionIds(selectedConnectionIds.filter((id) => id !== connectionId));
                  }}
                  placeholder="Search for connections..."
                  isMulti={true}
                />
              </div>
              <div>
                <FieldLabel label="Connection files preview" />
                <ConnectionFilesTable
                  hideCheckboxColumn={true}
                  connectionFiles={connectionFiles}
                  setConnectionFiles={setConnectionFiles}
                  columns={['File name', 'Status', 'Modified at', 'Created at']}
                  connectionIds={selectedConnectionIds}
                />
              </div>
            </div>
          </StepperStep>
        )}
      </Stepper>
    </Modal>
  );
}

const styles = StyleSheet.create({
  connectionTabBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
});
